import React, { useEffect, useState } from "react";
import api, { resolveMediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus, Save, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

/* Reusable file uploader */
export function FileUploader({ kind = "photo", onUploaded, label, testId, small = false }) {
  const [busy, setBusy] = useState(false);
  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded?.(data.url);
      toast.success(`Uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <label className={`cursor-pointer inline-flex items-center gap-2 rounded-full border-2 border-dashed border-brand-navy/25 hover:border-brand-orange transition ${small ? "px-3 py-1.5 text-xs" : "px-4 py-2.5 text-sm"} ${busy ? "opacity-70 pointer-events-none" : ""}`} data-testid={testId}>
      {busy ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
      <span className="text-brand-navy font-medium">{busy ? "Uploading…" : (label || (kind === "video" ? "Upload video" : "Upload photo"))}</span>
      <input type="file" accept={kind === "video" ? "video/*" : "image/*"} onChange={handleChange} disabled={busy} className="hidden" />
    </label>
  );
}

/* -------------------------- CHEFS -------------------------- */
export function ChefsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty());
  function empty() { return { name: "", experience_years: 0, order: 0, photo_url: "", r_tr:"", r_ar:"", r_en:"", r_ru:"", b_tr:"", b_ar:"", b_en:"", b_ru:"" }; }
  const load = () => api.get("/chefs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/chefs", {
    name: form.name, experience_years: Number(form.experience_years) || 0, order: Number(form.order) || 0, photo_url: form.photo_url,
    role: { tr: form.r_tr, ar: form.r_ar, en: form.r_en, ru: form.r_ru },
    bio: { tr: form.b_tr, ar: form.b_ar, en: form.b_en, ru: form.b_ru },
  }); setForm(empty()); toast.success("Saved"); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/chefs/${id}`); load(); };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      <form onSubmit={submit} className="bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="chef-form">
        <h3 className="font-serif text-xl text-brand-navy">Add Chef</h3>
        <Input placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} required />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Years" type="number" value={form.experience_years} onChange={(e) => setForm({...form, experience_years:e.target.value})} />
          <Input placeholder="Order" type="number" value={form.order} onChange={(e) => setForm({...form, order:e.target.value})} />
        </div>
        <div className="flex items-center gap-2">
          {form.photo_url && <img src={resolveMediaUrl(form.photo_url)} alt="" className="w-14 h-14 rounded-full object-cover"/>}
          <FileUploader small kind="photo" testId="chef-photo-upload" onUploaded={(url) => setForm({...form, photo_url:url})} />
        </div>
        <Input placeholder="Role TR" value={form.r_tr} onChange={(e) => setForm({...form, r_tr:e.target.value})} />
        <Input placeholder="Role AR" dir="rtl" value={form.r_ar} onChange={(e) => setForm({...form, r_ar:e.target.value})} />
        <Input placeholder="Role EN" value={form.r_en} onChange={(e) => setForm({...form, r_en:e.target.value})} />
        <Input placeholder="Role RU" value={form.r_ru} onChange={(e) => setForm({...form, r_ru:e.target.value})} />
        <Textarea placeholder="Bio TR" value={form.b_tr} onChange={(e) => setForm({...form, b_tr:e.target.value})} rows={3} />
        <Textarea placeholder="Bio AR" dir="rtl" value={form.b_ar} onChange={(e) => setForm({...form, b_ar:e.target.value})} rows={3} />
        <Textarea placeholder="Bio EN" value={form.b_en} onChange={(e) => setForm({...form, b_en:e.target.value})} rows={3} />
        <Textarea placeholder="Bio RU" value={form.b_ru} onChange={(e) => setForm({...form, b_ru:e.target.value})} rows={3} />
        <Button type="submit" className="bg-brand-orange text-white" data-testid="chef-submit"><Plus size={14}/> Add</Button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {items.map((c) => (
          <div key={c.id} className="bg-white p-4 rounded-2xl border border-brand-gold/20 flex gap-4 items-center" data-testid={`chef-item-${c.id}`}>
            {c.photo_url && <img src={resolveMediaUrl(c.photo_url)} alt="" className="w-16 h-16 rounded-full object-cover"/>}
            <div className="flex-1">
              <div className="font-serif text-lg text-brand-navy">{c.name}</div>
              <div className="text-xs text-slate-500">{c.role?.en} · {c.experience_years} yrs</div>
            </div>
            <button onClick={() => del(c.id)} className="text-red-600" data-testid={`chef-delete-${c.id}`}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------- TESTIMONIALS -------------------------- */
export function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty());
  function empty() { return { name: "", city: "", country: "", rating: 5, order: 0, photo_url: "", video_url: "", q_tr:"", q_ar:"", q_en:"", q_ru:"" }; }
  const load = () => api.get("/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/testimonials", {
    name: form.name, city: form.city, country: form.country, rating: Number(form.rating)||5, order: Number(form.order)||0,
    photo_url: form.photo_url, video_url: form.video_url,
    quote: { tr: form.q_tr, ar: form.q_ar, en: form.q_en, ru: form.q_ru },
  }); setForm(empty()); toast.success("Saved"); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/testimonials/${id}`); load(); };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      <form onSubmit={submit} className="bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="testimonial-form">
        <h3 className="font-serif text-xl text-brand-navy">Add Testimonial</h3>
        <Input placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} required />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="City" value={form.city} onChange={(e) => setForm({...form, city:e.target.value})} />
          <Input placeholder="Country" value={form.country} onChange={(e) => setForm({...form, country:e.target.value})} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Rating (1-5)" type="number" step="0.5" min="1" max="5" value={form.rating} onChange={(e) => setForm({...form, rating:e.target.value})} />
          <Input placeholder="Order" type="number" value={form.order} onChange={(e) => setForm({...form, order:e.target.value})} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FileUploader small kind="photo" testId="testimonial-photo-upload" label={form.photo_url?"Change photo":"+ Photo"} onUploaded={(url)=>setForm({...form, photo_url:url})} />
          <FileUploader small kind="video" testId="testimonial-video-upload" label={form.video_url?"Change video":"+ Video"} onUploaded={(url)=>setForm({...form, video_url:url})} />
        </div>
        <Textarea placeholder="Quote TR" value={form.q_tr} onChange={(e) => setForm({...form, q_tr:e.target.value})} rows={3} />
        <Textarea placeholder="Quote AR" dir="rtl" value={form.q_ar} onChange={(e) => setForm({...form, q_ar:e.target.value})} rows={3} />
        <Textarea placeholder="Quote EN" value={form.q_en} onChange={(e) => setForm({...form, q_en:e.target.value})} rows={3} />
        <Textarea placeholder="Quote RU" value={form.q_ru} onChange={(e) => setForm({...form, q_ru:e.target.value})} rows={3} />
        <Button type="submit" className="bg-brand-orange text-white" data-testid="testimonial-submit"><Plus size={14}/> Add</Button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {items.map((t) => (
          <div key={t.id} className="bg-white p-4 rounded-2xl border border-brand-gold/20 flex justify-between items-start gap-3" data-testid={`testimonial-item-${t.id}`}>
            <div className="flex-1">
              <div className="font-serif text-brand-navy">{t.name} <span className="text-xs text-slate-500 font-sans">· {t.city}{t.country?`, ${t.country}`:""}</span></div>
              <div className="text-sm text-slate-600 italic mt-1 line-clamp-2">"{t.quote?.en || t.quote?.tr}"</div>
            </div>
            <button onClick={() => del(t.id)} className="text-red-600" data-testid={`testimonial-delete-${t.id}`}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------- CERTIFICATES -------------------------- */
export function CertificatesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty());
  function empty() { return { title_tr:"", title_ar:"", title_en:"", title_ru:"", issuer:"", image_url:"", doc_url:"", order:0 }; }
  const load = () => api.get("/certificates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/certificates", {
    title: { tr: form.title_tr, ar: form.title_ar, en: form.title_en, ru: form.title_ru },
    issuer: form.issuer, image_url: form.image_url, doc_url: form.doc_url, order: Number(form.order)||0,
  }); setForm(empty()); toast.success("Saved"); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/certificates/${id}`); load(); };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      <form onSubmit={submit} className="bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="cert-form">
        <h3 className="font-serif text-xl text-brand-navy">Add Certificate</h3>
        <Input placeholder="Title TR" value={form.title_tr} onChange={(e) => setForm({...form, title_tr:e.target.value})} required />
        <Input placeholder="Title AR" dir="rtl" value={form.title_ar} onChange={(e) => setForm({...form, title_ar:e.target.value})} />
        <Input placeholder="Title EN" value={form.title_en} onChange={(e) => setForm({...form, title_en:e.target.value})} />
        <Input placeholder="Title RU" value={form.title_ru} onChange={(e) => setForm({...form, title_ru:e.target.value})} />
        <Input placeholder="Issuer (e.g. Saudi FDA)" value={form.issuer} onChange={(e) => setForm({...form, issuer:e.target.value})} />
        <div className="flex items-center gap-2">
          {form.image_url && <img src={resolveMediaUrl(form.image_url)} alt="" className="h-12 object-contain" />}
          <FileUploader small kind="photo" testId="cert-image-upload" onUploaded={(url) => setForm({...form, image_url:url})} />
        </div>
        <Input placeholder="Document URL (optional)" value={form.doc_url} onChange={(e) => setForm({...form, doc_url:e.target.value})} />
        <Input placeholder="Order" type="number" value={form.order} onChange={(e) => setForm({...form, order:e.target.value})} />
        <Button type="submit" className="bg-brand-orange text-white" data-testid="cert-submit"><Plus size={14}/> Add</Button>
      </form>
      <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map((c) => (
          <div key={c.id} className="bg-white p-4 rounded-2xl border border-brand-gold/20 flex flex-col items-center gap-2" data-testid={`cert-item-${c.id}`}>
            {c.image_url && <img src={resolveMediaUrl(c.image_url)} alt="" className="h-16 object-contain"/>}
            <div className="text-sm text-brand-navy text-center">{c.title?.en || c.title?.tr}</div>
            <div className="text-[10px] text-slate-500">{c.issuer}</div>
            <button onClick={() => del(c.id)} className="text-red-600 text-xs" data-testid={`cert-delete-${c.id}`}><Trash2 size={14}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------- FAQ -------------------------- */
export function FaqAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty());
  function empty() { return { q_tr:"", q_ar:"", q_en:"", q_ru:"", a_tr:"", a_ar:"", a_en:"", a_ru:"", category:"general", order:0 }; }
  const load = () => api.get("/faqs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/faqs", {
    question: { tr: form.q_tr, ar: form.q_ar, en: form.q_en, ru: form.q_ru },
    answer: { tr: form.a_tr, ar: form.a_ar, en: form.a_en, ru: form.a_ru },
    category: form.category, order: Number(form.order)||0,
  }); setForm(empty()); toast.success("Saved"); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/faqs/${id}`); load(); };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      <form onSubmit={submit} className="bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="faq-form">
        <h3 className="font-serif text-xl text-brand-navy">Add FAQ</h3>
        <Input placeholder="Question TR" value={form.q_tr} onChange={(e) => setForm({...form, q_tr:e.target.value})} required />
        <Input placeholder="Question AR" dir="rtl" value={form.q_ar} onChange={(e) => setForm({...form, q_ar:e.target.value})} />
        <Input placeholder="Question EN" value={form.q_en} onChange={(e) => setForm({...form, q_en:e.target.value})} />
        <Input placeholder="Question RU" value={form.q_ru} onChange={(e) => setForm({...form, q_ru:e.target.value})} />
        <Textarea placeholder="Answer TR" value={form.a_tr} onChange={(e) => setForm({...form, a_tr:e.target.value})} rows={3} />
        <Textarea placeholder="Answer AR" dir="rtl" value={form.a_ar} onChange={(e) => setForm({...form, a_ar:e.target.value})} rows={3} />
        <Textarea placeholder="Answer EN" value={form.a_en} onChange={(e) => setForm({...form, a_en:e.target.value})} rows={3} />
        <Textarea placeholder="Answer RU" value={form.a_ru} onChange={(e) => setForm({...form, a_ru:e.target.value})} rows={3} />
        <Input placeholder="Order" type="number" value={form.order} onChange={(e) => setForm({...form, order:e.target.value})} />
        <Button type="submit" className="bg-brand-orange text-white" data-testid="faq-submit"><Plus size={14}/> Add</Button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {items.map((f) => (
          <div key={f.id} className="bg-white p-4 rounded-2xl border border-brand-gold/20 flex justify-between items-start gap-3" data-testid={`faq-item-${f.id}`}>
            <div className="flex-1">
              <div className="font-medium text-brand-navy">{f.question?.en || f.question?.tr}</div>
              <div className="text-sm text-slate-600 mt-1 line-clamp-2">{f.answer?.en || f.answer?.tr}</div>
            </div>
            <button onClick={() => del(f.id)} className="text-red-600" data-testid={`faq-delete-${f.id}`}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------- BLOG -------------------------- */
export function BlogAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty());
  function empty() { return { slug:"", cover_image:"", tags:"", featured:false, published:true, t_tr:"", t_ar:"", t_en:"", t_ru:"", e_tr:"", e_ar:"", e_en:"", e_ru:"", b_tr:"", b_ar:"", b_en:"", b_ru:"" }; }
  const load = () => api.get("/posts", { params: { published: undefined } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); await api.post("/posts", {
    slug: form.slug, cover_image: form.cover_image, tags: form.tags.split(",").map(s=>s.trim()).filter(Boolean),
    featured: form.featured, published: form.published,
    title: { tr: form.t_tr, ar: form.t_ar, en: form.t_en, ru: form.t_ru },
    excerpt: { tr: form.e_tr, ar: form.e_ar, en: form.e_en, ru: form.e_ru },
    body: { tr: form.b_tr, ar: form.b_ar, en: form.b_en, ru: form.b_ru },
  }); setForm(empty()); toast.success("Saved"); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/posts/${id}`); load(); };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      <form onSubmit={submit} className="bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="post-form">
        <h3 className="font-serif text-xl text-brand-navy">Add Blog Post</h3>
        <Input placeholder="Slug (leave empty to auto)" value={form.slug} onChange={(e) => setForm({...form, slug:e.target.value})} />
        <div className="flex items-center gap-2">
          {form.cover_image && <img src={resolveMediaUrl(form.cover_image)} alt="" className="h-14 w-24 object-cover rounded"/>}
          <FileUploader small kind="photo" testId="post-cover-upload" label={form.cover_image?"Change cover":"+ Cover"} onUploaded={(url) => setForm({...form, cover_image:url})} />
        </div>
        <Input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({...form, tags:e.target.value})} />
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.featured} onChange={(e)=>setForm({...form, featured:e.target.checked})}/> Featured</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.published} onChange={(e)=>setForm({...form, published:e.target.checked})}/> Published</label>
        </div>
        <Input placeholder="Title TR" value={form.t_tr} onChange={(e) => setForm({...form, t_tr:e.target.value})} required />
        <Input placeholder="Title AR" dir="rtl" value={form.t_ar} onChange={(e) => setForm({...form, t_ar:e.target.value})} />
        <Input placeholder="Title EN" value={form.t_en} onChange={(e) => setForm({...form, t_en:e.target.value})} />
        <Input placeholder="Title RU" value={form.t_ru} onChange={(e) => setForm({...form, t_ru:e.target.value})} />
        <Textarea placeholder="Excerpt TR" value={form.e_tr} onChange={(e) => setForm({...form, e_tr:e.target.value})} rows={2} />
        <Textarea placeholder="Excerpt AR" dir="rtl" value={form.e_ar} onChange={(e) => setForm({...form, e_ar:e.target.value})} rows={2} />
        <Textarea placeholder="Excerpt EN" value={form.e_en} onChange={(e) => setForm({...form, e_en:e.target.value})} rows={2} />
        <Textarea placeholder="Excerpt RU" value={form.e_ru} onChange={(e) => setForm({...form, e_ru:e.target.value})} rows={2} />
        <Textarea placeholder="Body TR (markdown supported)" value={form.b_tr} onChange={(e) => setForm({...form, b_tr:e.target.value})} rows={6} />
        <Textarea placeholder="Body AR" dir="rtl" value={form.b_ar} onChange={(e) => setForm({...form, b_ar:e.target.value})} rows={6} />
        <Textarea placeholder="Body EN" value={form.b_en} onChange={(e) => setForm({...form, b_en:e.target.value})} rows={6} />
        <Textarea placeholder="Body RU" value={form.b_ru} onChange={(e) => setForm({...form, b_ru:e.target.value})} rows={6} />
        <Button type="submit" className="bg-brand-orange text-white" data-testid="post-submit"><Plus size={14}/> Add Post</Button>
      </form>
      <div className="lg:col-span-2 space-y-3">
        {items.map((p) => (
          <div key={p.id} className="bg-white p-4 rounded-2xl border border-brand-gold/20 flex gap-3 items-center" data-testid={`post-item-${p.id}`}>
            {p.cover_image && <img src={resolveMediaUrl(p.cover_image)} alt="" className="w-24 h-16 object-cover rounded"/>}
            <div className="flex-1">
              <div className="font-serif text-brand-navy">{p.title?.en || p.title?.tr}</div>
              <div className="text-xs text-slate-500">{p.slug} · {p.published?"published":"draft"}</div>
            </div>
            <button onClick={() => del(p.id)} className="text-red-600" data-testid={`post-delete-${p.id}`}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------- INSTAGRAM POSTS -------------------------- */
export function InstagramPostsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty());
  function empty() { return { url: "", caption: "", order: 0 }; }
  const load = () => api.get("/instagram_posts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/instagram_posts", { url: form.url.trim(), caption: form.caption, order: Number(form.order) || 0 });
      setForm(empty());
      toast.success("Instagram post eklendi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "URL geçersiz. Örn: https://www.instagram.com/p/XXXX/");
    }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/instagram_posts/${id}`); load(); };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      <form onSubmit={submit} className="bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="ig-post-form">
        <h3 className="font-serif text-xl text-brand-navy">Add Instagram Post</h3>
        <p className="text-xs text-slate-500 -mt-1">
          Paste a full Instagram permalink (post, reel or tv):<br />
          <code className="text-brand-orange">https://www.instagram.com/p/CxYz.../</code>
        </p>
        <Input placeholder="https://www.instagram.com/p/XXXX/" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} required data-testid="ig-post-url" />
        <Input placeholder="Caption (optional)" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="ig-post-caption" />
        <Input placeholder="Order (smaller = first)" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="ig-post-order" />
        <Button type="submit" className="bg-brand-orange text-white" data-testid="ig-post-submit"><Plus size={14} /> Add</Button>
      </form>
      <div className="lg:col-span-2 space-y-3" data-testid="ig-post-list">
        {items.length === 0 && (
          <div className="bg-white p-4 rounded-2xl border border-dashed border-brand-gold/40 text-sm text-slate-500">
            Henüz Instagram gönderisi eklenmedi. Sağdaki formdan URL yapıştırın.
          </div>
        )}
        {items.map((p) => (
          <div key={p.id} className="bg-white p-4 rounded-2xl border border-brand-gold/20 flex justify-between items-start gap-3" data-testid={`ig-post-item-${p.id}`}>
            <div className="flex-1 min-w-0">
              <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-brand-orange hover:underline text-sm font-mono truncate block">{p.url}</a>
              {p.caption && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{p.caption}</div>}
              <div className="text-[10px] text-slate-400 mt-1">Order: {p.order}</div>
            </div>
            <button onClick={() => del(p.id)} className="text-red-600" data-testid={`ig-post-delete-${p.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------- SETTINGS -------------------------- */
export function SettingsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/settings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (key, value) => { await api.patch(`/settings/${key}`, { key, value }); toast.success("Saved"); };
  const labels = {
    whatsapp_number: "WhatsApp Number (e.g. +90 555 123 4567)",
    whatsapp_message: "WhatsApp default message",
    instagram_url: "Instagram URL",
    hero_video_url: "Hero background video URL (leave empty for image)",
    site_email: "Contact email",
    site_phone: "Site phone (footer)",
    scene_3d_density: "3D food scene density — heavy | medium | off",
    scene_3d_theme: "3D scene food theme (dropdown below)",
    instagram_autoplay_seconds: "Instagram carousel autoplay (seconds; 0 = off, 4-8 recommended)",
    acente_support_whatsapp: "Acente Paneli Danışma Hattı WhatsApp (E.164 örn. +9055...)",
  };
  return (
    <div className="mt-6 grid gap-4 max-w-2xl" data-testid="settings-list">
      {items.map((s) => (
        <div key={s.key} className="bg-white p-4 rounded-2xl border border-brand-gold/20" data-testid={`setting-row-${s.key}`}>
          <Label className="text-xs uppercase tracking-widest text-brand-navy/70">{labels[s.key] || s.key}</Label>
          <div className="mt-2 flex items-center gap-2">
            {s.key === "scene_3d_density" ? (
              <select
                defaultValue={s.value || "medium"}
                onChange={(e) => save(s.key, e.target.value)}
                data-testid={`setting-input-${s.key}`}
                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
              >
                <option value="heavy">heavy</option>
                <option value="medium">medium</option>
                <option value="off">off</option>
              </select>
            ) : s.key === "scene_3d_theme" ? (
              <select
                defaultValue={s.value || "all"}
                onChange={(e) => save(s.key, e.target.value)}
                data-testid={`setting-input-${s.key}`}
                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm"
              >
                <option value="all">all — tüm ikonlar</option>
                <option value="desserts">desserts — tatlılar</option>
                <option value="savory">savory — ana yemekler</option>
                <option value="beverages">beverages — içecekler</option>
                <option value="ramadan">ramadan — Ramazan</option>
                <option value="summer">summer — yaz</option>
                <option value="winter">winter — kış</option>
                <option value="umrah">umrah — Umre (sade)</option>
              </select>
            ) : (
              <Input defaultValue={s.value} onBlur={(e) => save(s.key, e.target.value)} data-testid={`setting-input-${s.key}`} />
            )}
            {s.key === "hero_video_url" && (
              <FileUploader small kind="video" testId="setting-hero-video-upload" label="+ Video" onUploaded={(url) => save("hero_video_url", url).then(load)} />
            )}
          </div>
        </div>
      ))}
      <p className="text-xs text-slate-500">Changes save on blur (click away from the field).</p>
    </div>
  );
}
